import React, { useState, useEffect } from "react";
import { subscriptionPlanAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { MODULE_OPTIONS, moduleLabel } from "../../lib/modules";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Plus, Loader2, Pencil, Trash2, Package, Layers } from "lucide-react";

const emptyForm = { name: "", modules: [], max_employees: 50, price: "", description: "", status: "active" };

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await subscriptionPlanAPI.getAll();
      setPlans(res.data);
    } catch (error) {
      toast.error("Failed to fetch subscription plans");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name,
      modules: plan.modules || [],
      max_employees: plan.max_employees,
      price: plan.price ?? "",
      description: plan.description || "",
      status: plan.status || "active",
    });
    setShowDialog(true);
  };

  const toggleModule = (key) => {
    setForm((prev) => ({
      ...prev,
      modules: prev.modules.includes(key) ? prev.modules.filter((m) => m !== key) : [...prev.modules, key],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (form.modules.length === 0) {
      toast.error("Select at least one module");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      modules: form.modules,
      max_employees: Number(form.max_employees) || 1,
      price: form.price === "" ? null : Number(form.price),
      description: form.description || null,
      status: form.status,
    };
    try {
      if (editingId) {
        await subscriptionPlanAPI.update(editingId, payload);
        toast.success("Plan updated");
      } else {
        await subscriptionPlanAPI.create(payload);
        toast.success("Plan created");
      }
      setShowDialog(false);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await subscriptionPlanAPI.delete(plan.id);
      toast.success("Plan deleted");
      fetchPlans();
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="subscription-plans-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Subscription Plans</h1>
          <p className="text-slate-500">Create custom plans and the modules each one unlocks for tenants</p>
        </div>
        <Button className="btn-primary" onClick={openCreate} data-testid="create-plan-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Plan
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Max Employees</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No subscription plans yet. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                plans.map((plan) => (
                  <TableRow key={plan.id} data-testid={`plan-row-${plan.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Layers className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium">{plan.name}</p>
                          {plan.description && <p className="text-xs text-slate-400">{plan.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {plan.modules.slice(0, 5).map((m) => (
                          <Badge key={m} variant="outline" className="text-xs">{moduleLabel(m)}</Badge>
                        ))}
                        {plan.modules.length > 5 && (
                          <Badge variant="outline" className="text-xs">+{plan.modules.length - 5} more</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{plan.max_employees}</TableCell>
                    <TableCell>{plan.price != null ? `$${plan.price}` : "-"}</TableCell>
                    <TableCell>
                      <Badge variant={plan.status === "active" ? "default" : "destructive"} className="capitalize">{plan.status}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(plan.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(plan)} data-testid={`delete-plan-${plan.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Edit Plan" : "Create Plan"}</DialogTitle>
            <DialogDescription>Name the plan and choose which modules it unlocks for a tenant.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="form-group">
              <Label>Plan Name</Label>
              <Input placeholder="Plan1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="plan-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-group">
                <Label>Max Employees</Label>
                <Input type="number" min="1" value={form.max_employees} onChange={(e) => setForm({ ...form, max_employees: e.target.value })} required data-testid="plan-max-employees-input" />
              </div>
              <div className="form-group">
                <Label>Price (optional)</Label>
                <Input type="number" min="0" step="0.01" placeholder="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="plan-price-input" />
              </div>
            </div>
            <div className="form-group">
              <Label>Description (optional)</Label>
              <Textarea rows={2} placeholder="Short description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="plan-description-input" />
            </div>
            <div className="form-group">
              <Label className="mb-2 block">Modules ({form.modules.length} selected)</Label>
              <div className="grid grid-cols-2 gap-2 border border-slate-200 rounded-lg p-3 max-h-60 overflow-y-auto">
                {MODULE_OPTIONS.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 cursor-pointer" data-testid={`plan-module-${m.key}`}>
                    <Checkbox checked={form.modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} />
                    <span className="text-sm">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={saving} data-testid="submit-plan">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Save Changes" : "Create Plan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPlansPage;
